import React, { useMemo, useState } from 'react';
import { Box, Flex, NavLink, Text } from 'theme-ui';

const toRanges = (nums) => {
  if (!nums?.length) return [];
  const ranges = [];
  let s = nums[0], p = nums[0];
  for (let i = 1; i <= nums.length; i++) {
    const n = nums[i];
    if (n === p + 1) { p = n; continue; }
    ranges.push({ start: s, end: p });
    s = p = n;
  }
  return ranges;
};

const rangesToText = (ranges) => {
  if (!ranges.length) return '';
  const parts = ranges.map(({ start, end }) => (start === end ? `${start}` : `${start}–${end}`));
  return parts.join(', ');
};

const groupCitations = (items) => {
  const map = new Map();
  for (const c of items || []) {
    if (!c?.file_path) continue;
    const g = map.get(c.file_path) ?? { href: c.file_path, pages: new Set() };
    if (Number.isFinite(c.page_number)) g.pages.add(c.page_number);
    map.set(c.file_path, g);
  }
  return Array.from(map.values()).map((g) => {
    const pages = Array.from(g.pages).sort((a, b) => a - b);
    const ranges = toRanges(pages);
    const rangeText = ranges.length ? `${ranges.length > 1 ? 'pp.' : 'p.'} ${rangesToText(ranges)}` : '';
    return { ...g, pages, ranges, rangeText };
  });
};

export const Citations = ({ citations, defaultOpen = false, maxChips = 5 }) => {
  const groups = useMemo(() => groupCitations(citations), [citations]);
  const [open, setOpen] = useState(defaultOpen);

  if (!groups.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Flex sx={{ alignItems: 'center', gap: 2, fontSize: 0, color: 'placeholder' }}>
        <Text as="span" sx={{ fontWeight: 600, color: 'placeholder', pt: "2px" }}>Sources</Text>
        <Text as="span" sx={{ color: 'placeholder' }}>({groups.length})</Text>
        <NavLink
          as="button"
          onClick={() => setOpen((v) => !v)}
          sx={{
            ml: 1, fontSize: 0, px: 0, py: 0, bg: 'transparent', border: 0,
            color: 'placeholder', cursor: 'pointer', textDecoration: 'underline',
          }}
        >
          {open ? 'Hide' : 'Show'}
        </NavLink>
      </Flex>

      {open && (
        <Box
          as="ul"
          sx={{ listStyle: 'none', p: 0, m: 0, mt: 2, display: 'grid', gap: 2 }}
        >
          {groups.map((g, i) => {
            const shownRanges = g.ranges.slice(0, maxChips);
            const extra = Math.max(0, g.ranges.length - shownRanges.length);
            return (
              <Box
                as="li"
                key={g.href}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  alignItems: 'center',
                  gap: 1,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'placeholder',
                  borderRadius: 10,
                  bg: 'background',
                }}
              >
                <Box
                  sx={{
                    width: 18, height: 18, borderRadius: 9999, bg: 'placeholder', opacity: 0.3,
                    display: 'grid', placeItems: 'center', fontSize: 0, fontWeight: 700, color: 'text',
                  }}
                >
                  {i + 1}
                </Box>

                <Flex sx={{ flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <NavLink
                    href={g.href}
                    target="_blank"
                    sx={{ fontSize: 0, color: 'text', textDecoration: 'none', ':hover': { textDecoration: 'underline' } }}
                  >
                    Open file
                  </NavLink>

                  <Text as="span" sx={{ color: 'placeholder', fontSize: 0, ml: 'auto' }}>
                    {g.rangeText}
                  </Text>

                  <Flex sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {shownRanges.map((r) => (
                      <NavLink
                        key={`${r.start}-${r.end}`}
                        href={`${g.href}#page=${r.start}`} // link only first page of each range
                        target="_blank"
                        aria-label={`Open p.${r.start}${r.end !== r.start ? ` (covers ${r.start}–${r.end})` : ''}`}
                        sx={{
                          fontSize: 0, px: 2, py: '2px', lineHeight: 1,
                          border: '1px solid', borderColor: 'tealLight',
                          borderRadius: 9999, color: 'tealLight',
                          textDecoration: 'none', bg: 'transparent',
                        }}
                      >
                        p.{r.start}
                      </NavLink>
                    ))}
                    {extra > 0 && (
                      <Text as="span" sx={{ fontSize: 0, color: 'placeholder' }}>
                        +{extra} more
                      </Text>
                    )}
                  </Flex>
                </Flex>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
